import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Camera, Upload, X, Loader2, User } from "lucide-react";

const isProduction = typeof window !== 'undefined' && (
  window.location.hostname === 'rekrutor.com' || 
  window.location.hostname === 'www.rekrutor.com' ||
  window.location.hostname.endsWith('.emergent.host')
);
const API = isProduction ? '/api' : `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const ProfilePictureUpload = ({ 
  currentImage, 
  onUploadSuccess, 
  uploadType = "profile", // "profile" or "company"
  size = "large" // "small", "medium", "large"
}) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileInputRef = useRef(null);

  const sizes = {
    small: { container: "w-16 h-16", icon: "w-6 h-6", button: "w-6 h-6" },
    medium: { container: "w-24 h-24", icon: "w-10 h-10", button: "w-8 h-8" },
    large: { container: "w-32 h-32", icon: "w-12 h-12", button: "w-10 h-10" }
  };

  const currentSize = sizes[size] || sizes.large;

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload a JPEG, PNG, WebP, or GIF image");
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB");
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);

    // Upload
    await uploadFile(file);
  };

  const uploadFile = async (file) => {
    setUploading(true);
    
    const formData = new FormData();
    formData.append("file", file);

    const endpoint = uploadType === "company" 
      ? `${API}/profile/upload-company-logo`
      : `${API}/profile/upload-picture`;

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(uploadType === "company" ? "Company logo updated!" : "Profile picture updated!");
        onUploadSuccess?.(data.picture_url || data.logo_url);
        setPreview(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Upload failed");
        setPreview(null);
      }
    } catch (error) {
      toast.error("Upload failed. Please try again.");
      setPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const clearPreview = () => {
    setPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const displayImage = preview || currentImage;

  return (
    <div className="relative inline-block">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handleFileSelect}
        className="hidden"
        data-testid="profile-picture-input"
      />

      {/* Image container */}
      <div 
        className={`${currentSize.container} bg-obsidian-light border-2 border-[#333] overflow-hidden flex items-center justify-center relative group cursor-pointer hover:border-crimson transition-colors`}
        onClick={handleClick}
      >
        {displayImage ? (
          <img 
            src={displayImage.startsWith("/api") ? (isProduction ? displayImage : `${process.env.REACT_APP_BACKEND_URL}${displayImage}`) : displayImage}
            alt="Profile" 
            className="w-full h-full object-cover"
          />
        ) : (
          <User className={`${currentSize.icon} text-gray-600`} />
        )}

        {/* Overlay on hover */}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          {uploading ? (
            <Loader2 className="w-6 h-6 text-white animate-spin" />
          ) : (
            <Camera className="w-6 h-6 text-white" />
          )}
        </div>

        {/* Loading overlay */}
        <AnimatePresence>
          {uploading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/80 flex items-center justify-center"
            >
              <Loader2 className="w-8 h-8 text-crimson animate-spin" />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Upload button */}
      <button
        onClick={handleClick}
        disabled={uploading}
        className={`absolute -bottom-1 -right-1 ${currentSize.button} bg-crimson hover:bg-crimson-hover text-white rounded-full flex items-center justify-center shadow-lg transition-colors`}
        data-testid="upload-picture-btn"
      >
        {uploading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Camera className="w-4 h-4" />
        )}
      </button>

      {/* Clear preview button */}
      {preview && !uploading && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            clearPreview();
          }}
          className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-lg"
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
};

export default ProfilePictureUpload;
